import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducers';

import SearchBar from '../../../Components/SearchBar';
import TripCategoryFilter from '../../../Components/TripCategoryFilter';
import TripCard from '../TripCard';
import { fetchCategories } from '../../../services/CommonApiCall';
import { fetchExploreTrips } from '../../../services/CommonFunctions';
import { ITrip } from '../Trips';

interface ExploreTabProps {
  onTripClick: (trip: any) => void;
}

const ExploreTab: React.FC<ExploreTabProps> = ({ onTripClick }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredTrips, setFilteredTrips] = useState<ITrip[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const categories = useSelector((state: RootState) => state.category?.categories || []);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchCategories(dispatch, 'trip-itinerary');
  }, [dispatch]);

  // Fetch trips with pagination
  const fetchTrips = async (page: number = 1, reset: boolean = false) => {
    setLoading(true);
    if (reset) {
      setInitialLoading(true);
    }

    try {
      const result = await fetchExploreTrips(
        page, 
        5, // limit
        searchTerm || undefined, 
        selectedCategory !== 'All' ? selectedCategory : undefined
      );

      if (result.data) {
        if (reset) {
          setFilteredTrips(result.data);
        } else {
          setFilteredTrips(prev => [...prev, ...result.data]);
        }
        
        setHasMore(result.hasMore);
      }
    } catch (err) {
      console.log('error', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // Initial load and reset when search/category changes
  useEffect(() => {
    setCurrentPage(1);
    setFilteredTrips([]);
    fetchTrips(1, true);
  }, [searchTerm, selectedCategory]);

  // Load more function
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchTrips(nextPage, false);
    }
  }, [loading, hasMore, currentPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadMore]);

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search all trips and itineraries"
      />

      <TripCategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
      />

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
            }}
          >
            {filteredTrips.map((trip) => (
              <TripCard
                key={trip._id}
                trip={trip}
                variant="explore"
                onClick={onTripClick}
              />
            ))}
          </Box>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <Box 
              ref={loadMoreRef}
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                py: 3,
                minHeight: '60px'
              }}
            >
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more trips...
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* No more data message */}
          {!hasMore && filteredTrips.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No more trips to load
              </Typography>
            </Box>
          )}

          {/* No data message */}
          {!initialLoading && filteredTrips.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No trips found
              </Typography>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default ExploreTab;
